"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { apiFetch } from "@/lib/api/apiFetch"

interface Contact {
  id: string
  email: string
  name: string
  status: string
}

interface ManageContactListsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedContacts: any[]
  setSelectedContacts: React.Dispatch<React.SetStateAction<any[]>>
  onStatusUpdated: () => void
}

/**
 * DisableContactDialog component
 * This component renders a dialog for disabling selected contacts.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.open - Whether the dialog is open or not.
 * @param {function} props.onOpenChange - The callback function for changing the open state of the dialog.
 * @param {Array<Object>} props.selectedContacts - The array of selected contacts.
 * @param {function} props.onStatusUpdated - The callback function for updating the status.
 * @returns {JSX.Element} The rendered DisableContactDialog component.
 * @author uplSouvik
 */


export function DisableContactDialog({
  open,
  onOpenChange,
  selectedContacts,
  setSelectedContacts,
  onStatusUpdated,
}: ManageContactListsDialogProps) {
  const [updating, setUpdating] = useState(false)
  const { toast } = useToast()

  // Handle disabling contacts
  const handleDisableContacts = async () => {
    if (selectedContacts.length === 0) {
      toast({
        title: "No contacts selected",
        description: "Please select at least one contact to disable",
        variant: "destructive",
      })
      return
    }

    try {
      setUpdating(true)

      const contactIds = selectedContacts.map(c => c.id)

      const data = await apiFetch("/api/contacts", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: {
          action: "disable",
          ids: contactIds
        }
      })

      toast({
        title: "Success",
        description: `Disabled ${data?.data?.disabledCount} contact(s)`,
      })

      onStatusUpdated()
      onOpenChange(false)
      setSelectedContacts([])
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to disable contacts",
        variant: "destructive",
      }
      )
    } finally {
      setUpdating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Disable Contact</DialogTitle>
          <DialogDescription>
            {selectedContacts.length} contact(s) selected: {selectedContacts.map(c => c.email).join(", ")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            disabled={updating}
            onClick={() => onOpenChange(false)}
            className="text-black"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            disabled={updating}
            className="bg-black text-white"
            onClick={handleDisableContacts}
          >
            Disable
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
